import punycode from "punycode";

// @ts-ignore FIXME: strict migration - Could not find a declaration file for module 'psl' — external package ships no types
import type { ParseError, ParsedDomain } from "psl";
// @ts-ignore FIXME: strict migration - Could not find a declaration file for module 'psl' — external package ships no types
import { parse } from "psl";

import Sentry from "@similarweb/sentry-logger";

const URL_STARTS_WITH_WWW_REGEX = /^www(\.|$)/i; // starts with 'www' followed by '.' or end of string
const URL_STARTS_WITH_PROTOCOL_REGEX = /^(https?:\/\/)/i; // starts with http:// or https://
// A single URL/domain token: optional protocol, a dotted host, and an optional path/query that
// stops at the first whitespace. Global so we can collect every token in a pasted blob.
const URL_CANDIDATE_REGEX = /(?:https?:\/\/)?(?:[\p{L}\d-]+\.)+[\p{L}\d-]+(?:[/?][^\s]*)?/giu;

const emptyUrlComponents = () => ({
    domain: undefined as string | undefined,
    subdomain: undefined as string | undefined,
    folder: "",
    queryParams: "",
    error: undefined as ParseError | undefined,
});

export const removeTrailingSlash = (str: string) => {
    return str?.replace(/\/+$/, "");
};

// Bare host of a candidate (drop protocol + path/query) so we can probe its TLD.
const getCandidateHostname = (candidate: string) =>
    candidate.replace(URL_STARTS_WITH_PROTOCOL_REGEX, "").split(/[/?]/)[0];

// True only when the TLD is a real, listed public suffix (e.g. ".com", ".co.il", ".рф").
// Run-on words and filenames like "end.Visit" or "report.pdf" are unlisted, so this filters them out.
const isListedPublicDomain = (host: string) => {
    const parsed = parse(host);
    return !("error" in parsed) && parsed.listed;
};

// Pick the single most likely URL out of noisy pasted text (timestamps, log lines, surrounding words):
// 1) an explicit http(s):// token — the strongest signal of what the user copied;
// 2) otherwise the first token with a real, listed TLD — skips filenames / run-on words;
// 3) otherwise the first dotted token — a safety net so anything that parsed before still parses.
const selectUrlCandidate = (url: string): string | undefined => {
    const candidates = url.trim().match(URL_CANDIDATE_REGEX);
    if (!candidates?.length) {
        return undefined;
    }
    return (
        candidates.find((candidate) => URL_STARTS_WITH_PROTOCOL_REGEX.test(candidate)) ??
        candidates.find((candidate) => isListedPublicDomain(getCandidateHostname(candidate))) ??
        candidates[0]
    );
};

export const getUrlComponents = (url: string) => {
    if (!url || typeof url !== "string") {
        return emptyUrlComponents();
    }

    // Parse only the extracted URL token, not the whole string — feeding noisy pasted text straight
    // to `new URL` throws on the surrounding characters and loses the URL the user actually meant.
    const candidate = selectUrlCandidate(url);
    if (!candidate) {
        return emptyUrlComponents();
    }

    const urlWithProtocol = candidate.match(URL_STARTS_WITH_PROTOCOL_REGEX) ? candidate : `https://${candidate}`;
    let hostname = "",
        folder = "",
        queryParams = "";

    try {
        const { hostname: h, pathname: p, search: s } = new URL(urlWithProtocol);
        hostname = punycode.toUnicode(h);
        folder = removeTrailingSlash(punycode.toUnicode(decodeURIComponent(p)));
        queryParams = removeTrailingSlash(
            punycode.toUnicode(decodeURIComponent(s)).substring(1), // remove question mark
        );
    } catch (error) {
        // The candidate looked like a URL but `new URL` still rejected it — rare and non-actionable.
        // Log at "warning" so a genuine systemic regression stays visible without error-level noise.
        Sentry.errorTracker({
            message: "getUrlComponents - failed to parse URL",
            e: error,
            level: "warning",
            params: { extra: { url, candidate } },
        });
        return emptyUrlComponents();
    }

    let domain: string | undefined, subdomain: string | undefined, parserError: ParseError | undefined;
    if (hostname?.length) {
        const { error, ...parsedQuery } = parse(hostname) as ParsedDomain;
        parserError = error;
        if (!parserError) {
            domain = parsedQuery.domain ?? parsedQuery.tld;

            const subdomainWithoutWWW = parsedQuery.subdomain?.replace(URL_STARTS_WITH_WWW_REGEX, "");
            subdomain = subdomainWithoutWWW || undefined;
        }
    }

    return { domain, subdomain, folder, queryParams, error: parserError };
};

export const getDomainWithSubdomain = (domain: string, subdomain: string) => {
    return subdomain ? `${subdomain}.${domain}` : domain;
};

export const getDomainWithSubs = (url: string) => {
    const { domain, subdomain } = getUrlComponents(url);
    // @ts-ignore FIXME: strict migration - Argument of type 'string | undefined' is not assignable to parameter of type 'string' — getDomainWithSubdomain tolerates undefined at runtime
    return getDomainWithSubdomain(domain, subdomain);
};

export const hasQueryParameters = (url: string) => getUrlComponents(url).queryParams?.length > 0;
